// Dev mock supabase client — emuluje supabase-js API z chainable query
// Používa sa iba keď REACT_APP_DEV_MOCK=true. Žiadny real network call.

import { createSeedStore, MOCK_SESSION, MOCK_USER } from './devMockData';

// Bump suffix whenever a new table is added to devMockData seed — forces
// existing dev sessions to re-seed instead of reading a stale cached store.
// Last bump: 2026-05-18 (sk_companies added for RPO autocomplete).
const STORAGE_KEY = 'todue-dev-mock-store-v2';
const LEGACY_STORAGE_KEYS = ['todue-dev-mock-store-v1'];

function loadStore() {
  // Always start from fresh seed shape so newly added tables (e.g. sk_companies
  // added 2026-05-18) appear without requiring every dev to reset cache.
  const fresh = createSeedStore();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      Object.keys(cached).forEach((k) => { fresh[k] = cached[k]; });
      // sk_companies + tag-like reference tables should always re-seed from the
      // codebase so new entries land even after the user has the store cached.
      const REFERENCE_TABLES = ['sk_companies'];
      REFERENCE_TABLES.forEach((k) => {
        const seedRows = createSeedStore()[k];
        if (Array.isArray(seedRows)) fresh[k] = seedRows;
      });
    }
  } catch (_e) {}
  try {
    LEGACY_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (_e) {}
  return fresh;
}

function saveStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (_e) {}
}

const store = loadStore();

// Public helper — zo settings/dev panela vieš resetnúť stav
export function resetDevMockStore() {
  const fresh = createSeedStore();
  Object.keys(store).forEach(k => delete store[k]);
  Object.assign(store, fresh);
  saveStore(store);
  if (typeof window !== 'undefined') window.location.reload();
}

if (typeof window !== 'undefined') {
  window.__todueDevMock = { store, reset: resetDevMockStore };
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function compare(a, b) {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
  return a > b ? 1 : -1;
}

function parseSelectJoins(selectStr) {
  // Detect patterns like 'client:clients(id, name, ico)'
  const joins = [];
  const re = /(\w+):(\w+)\(([^)]+)\)/g;
  let m;
  while ((m = re.exec(selectStr)) !== null) {
    joins.push({ alias: m[1], table: m[2], cols: m[3].split(',').map(s => s.trim()) });
  }
  return joins;
}

function applyJoins(rows, joins) {
  if (!joins.length) return rows;
  return rows.map(row => {
    const enriched = { ...row };
    joins.forEach(j => {
      const fkCol = `${j.alias}_id`;
      const target = (store[j.table] || []).find(r => r.id === row[fkCol]);
      if (target) {
        const projected = {};
        j.cols.forEach(c => { projected[c] = target[c]; });
        enriched[j.alias] = projected;
      } else {
        enriched[j.alias] = null;
      }
    });
    return enriched;
  });
}

class MockQuery {
  constructor(table) {
    this.table = table;
    this.op = 'select';
    this.payload = null;
    this.filters = [];
    this.orders = [];
    this.selectStr = '*';
    this.singleMode = null;
    this.limitN = null;
  }

  select(cols = '*') {
    this.selectStr = cols;
    return this;
  }

  eq(col, val) {
    this.filters.push({ type: 'eq', col, val });
    return this;
  }

  in(col, vals) {
    this.filters.push({ type: 'in', col, val: vals });
    return this;
  }

  neq(col, val) {
    this.filters.push({ type: 'neq', col, val });
    return this;
  }

  gte(col, val) {
    this.filters.push({ type: 'gte', col, val });
    return this;
  }

  lte(col, val) {
    this.filters.push({ type: 'lte', col, val });
    return this;
  }

  order(col, opts) {
    this.orders.push({ col, asc: opts?.ascending ?? true });
    return this;
  }

  limit(n) {
    this.limitN = n;
    return this;
  }

  single() {
    this.singleMode = 'single';
    return this;
  }

  maybeSingle() {
    this.singleMode = 'maybeSingle';
    return this;
  }

  insert(payload) {
    this.op = 'insert';
    this.payload = payload;
    return this;
  }

  update(patch) {
    this.op = 'update';
    this.payload = patch;
    return this;
  }

  delete() {
    this.op = 'delete';
    return this;
  }

  upsert(payload) {
    this.op = 'upsert';
    this.payload = payload;
    return this;
  }

  _filterRows(rows) {
    return rows.filter(r => this.filters.every(f => {
      if (f.type === 'eq') return r[f.col] === f.val;
      if (f.type === 'neq') return r[f.col] !== f.val;
      if (f.type === 'in') return f.val.includes(r[f.col]);
      if (f.type === 'gte') return r[f.col] >= f.val;
      if (f.type === 'lte') return r[f.col] <= f.val;
      return true;
    }));
  }

  _applyOrders(rows) {
    if (!this.orders.length) return rows;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      for (const o of this.orders) {
        const cmp = compare(a[o.col], b[o.col]);
        if (cmp !== 0) return o.asc ? cmp : -cmp;
      }
      return 0;
    });
    return sorted;
  }

  _wrap(rows) {
    if (this.singleMode === 'single') {
      return { data: rows[0] || null, error: rows[0] ? null : { message: 'JSON object requested, no rows returned', code: 'PGRST116' } };
    }
    if (this.singleMode === 'maybeSingle') {
      return { data: rows[0] || null, error: null };
    }
    return { data: rows, error: null };
  }

  async _run() {
    // Reference tables (read-only zoznamy ako sk_companies/RPO) sa môžu stratiť
    // po HMR reloadoch alebo keď cached store mal prázdne pole. Re-seed-uj
    // zo zdrojového kódu kedykoľvek je tabuľka prázdna.
    const REFERENCE_TABLES = ['sk_companies'];
    if (REFERENCE_TABLES.includes(this.table)) {
      if (!Array.isArray(store[this.table]) || store[this.table].length === 0) {
        store[this.table] = createSeedStore()[this.table] || [];
      }
    } else if (!store[this.table]) {
      store[this.table] = [];
    }
    const tableRows = store[this.table];
    const joins = parseSelectJoins(this.selectStr);

    if (this.op === 'select') {
      let rows = this._filterRows(tableRows);
      rows = this._applyOrders(rows);
      if (this.limitN != null) rows = rows.slice(0, this.limitN);
      rows = applyJoins(rows, joins);
      return this._wrap(rows);
    }

    if (this.op === 'insert' || this.op === 'upsert') {
      const items = Array.isArray(this.payload) ? this.payload : [this.payload];
      const inserted = items.map(p => ({
        id: p?.id || uuid(),
        created_at: p?.created_at || new Date().toISOString(),
        ...p,
      }));
      tableRows.push(...inserted);
      saveStore(store);
      const enriched = applyJoins(inserted, joins);
      return this._wrap(enriched);
    }

    if (this.op === 'update') {
      const matching = this._filterRows(tableRows);
      matching.forEach(r => Object.assign(r, this.payload));
      saveStore(store);
      const enriched = applyJoins(matching, joins);
      return this._wrap(enriched);
    }

    if (this.op === 'delete') {
      const toDelete = new Set(this._filterRows(tableRows));
      store[this.table] = tableRows.filter(r => !toDelete.has(r));
      saveStore(store);
      return { data: null, error: null };
    }

    return { data: null, error: { message: `Unknown op ${this.op}` } };
  }

  then(resolve, reject) {
    return this._run().then(resolve, reject);
  }
}

// ─── Auth ──────────────────────────────────────────────────────────────────

const authListeners = new Set();

const auth = {
  async getSession() {
    return { data: { session: MOCK_SESSION }, error: null };
  },
  async getUser() {
    return { data: { user: MOCK_USER }, error: null };
  },
  onAuthStateChange(cb) {
    authListeners.add(cb);
    setTimeout(() => cb('SIGNED_IN', MOCK_SESSION), 0);
    return {
      data: {
        subscription: {
          unsubscribe: () => authListeners.delete(cb),
        },
      },
    };
  },
  async signInWithOtp(_args) {
    return { data: {}, error: null };
  },
  async signInWithPassword(_args) {
    return { data: { session: MOCK_SESSION, user: MOCK_USER }, error: null };
  },
  async signInWithOAuth(_args) {
    setTimeout(() => authListeners.forEach(cb => cb('SIGNED_IN', MOCK_SESSION)), 0);
    return { data: { provider: _args?.provider || 'google', url: null }, error: null };
  },
  async signInWithIdToken(_args) {
    setTimeout(() => authListeners.forEach(cb => cb('SIGNED_IN', MOCK_SESSION)), 0);
    return { data: { session: MOCK_SESSION, user: MOCK_USER }, error: null };
  },
  async setSession(_args) {
    return { data: { session: MOCK_SESSION, user: MOCK_USER }, error: null };
  },
  async updateUser(_args) {
    return { data: { user: MOCK_USER }, error: null };
  },
  async resetPasswordForEmail(_email, _opts) {
    return { data: {}, error: null };
  },
  async signOut() {
    authListeners.forEach(cb => cb('SIGNED_OUT', null));
    return { error: null };
  },
};

// ─── Storage ───────────────────────────────────────────────────────────────

function makeStorageBucket(_bucketId) {
  return {
    async upload(path, _file, _opts) {
      return { data: { path }, error: null };
    },
    getPublicUrl(path) {
      return { data: { publicUrl: `https://dev-mock.local/${path}` } };
    },
    async remove(_paths) {
      return { data: [], error: null };
    },
    async createSignedUrl(path, _expiresIn) {
      return { data: { signedUrl: `https://dev-mock.local/${path}?signed=1` }, error: null };
    },
  };
}

const storage = {
  from: makeStorageBucket,
};

// ─── RPC (SECURITY DEFINER functions) ──────────────────────────────────────
// Dev mock len pre RPC mená ktoré frontend reálne volá. Default = null/no-op.

// HMR-safe lookup pre reference tables — modul-level `store` premenná môže byť
// stale po HMR reloadoch, takže pri každom volaní si vyzdvihneme čerstvý
// `sk_companies` zo seedu ak v store chýba alebo je prázdna.
function getSkCompanies() {
  if (!Array.isArray(store.sk_companies) || store.sk_companies.length === 0) {
    store.sk_companies = createSeedStore().sk_companies || [];
  }
  return store.sk_companies;
}

async function rpc(name, args) {
  switch (name) {
    case 'has_drive_access':
      // V dev-mock móde vrátime true aby Drive connect flow (popup + polling)
      // neskončil timeoutom — rovno signalizujeme že Drive je „pripojený".
      return { data: true, error: null };
    case 'search_sk_companies': {
      const q = String(args?.q || '').toLowerCase().trim();
      const max = Number(args?.max_results) || 10;
      if (q.length < 2) return { data: [], error: null };
      const numeric = q.replace(/\D/g, '');
      const rows = getSkCompanies();
      const matches = rows.filter((r) => {
        if (numeric && numeric.length >= 4 && r.ico?.startsWith(numeric)) return true;
        return r.name?.toLowerCase().includes(q);
      });
      matches.sort((a, b) => Number(!!b.is_active) - Number(!!a.is_active));
      return { data: matches.slice(0, max), error: null };
    }
    case 'apply_stock_movement': {
      const { p_item_id, p_delta } = args;
      if (!store.catalog_items) store.catalog_items = [];
      const item = store.catalog_items.find(r => r.id === p_item_id);
      if (!item) return { data: null, error: { message: 'Not found' } };
      item.stock_qty = (Number(item.stock_qty) || 0) + Number(p_delta);
      const movement = {
        id: uuid(),
        workspace_id: item.workspace_id,
        item_id: p_item_id,
        delta: p_delta,
        reason: args.p_reason,
        invoice_id: args.p_invoice_id,
        balance_after: item.stock_qty,
        created_at: new Date().toISOString()
      };
      if (!store.stock_movements) store.stock_movements = [];
      store.stock_movements.push(movement);
      saveStore(store);
      return { data: item.stock_qty, error: null };
    }
    case 'bump_item_usage': {
      const ids = args.p_item_ids || [];
      const items = (store.catalog_items || []).filter(r => ids.includes(r.id));
      items.forEach(it => {
        it.times_used = (it.times_used || 0) + 1;
        it.last_used_at = new Date().toISOString();
      });
      saveStore(store);
      return { data: null, error: null };
    }
    case 'next_invoice_number': {
      const ws = (store.workspaces || []).find(r => r.id === args.p_workspace_id);
      if (!ws) return { data: null, error: { message: 'WS not found' } };
      ws.fa_counter = (ws.fa_counter || 0) + 1;
      saveStore(store);
      return { data: [{ counter: ws.fa_counter, format: ws.fa_number_format }], error: null };
    }
    case 'purge_drive_credentials':
      return { data: null, error: null };
    default:
      return { data: null, error: null };
  }
}

// ─── Edge Functions ────────────────────────────────────────────────────────
// V dev-mock móde nemáme real Supabase Edge Functions — simulujeme úspešné
// odpovede pre Drive flow tak, aby UI nevybuchol na chýbajúce auth_url ani
// na find-orphaned-drive-files lookup.
const functions = {
  async invoke(name, _opts) {
    switch (name) {
      case 'drive-oauth-start':
        // about:blank otvorí popup ktorý sa hneď zavrie; polling sa následne
        // sám timeoutuje a UI zobrazí toast „Drive connect zlyhal".
        // Pre čistejšiu UX skôr UI mode: v dev-mock móde skry Drive card.
        return { data: { auth_url: 'about:blank' }, error: null };
      case 'drive-oauth-callback':
      case 'drive-token-refresh':
        return { data: { ok: true }, error: null };
      case 'find-orphaned-drive-files':
        return { data: { orphans: [] }, error: null };
      default:
        return { data: null, error: null };
    }
  },
};

// ─── Public client ─────────────────────────────────────────────────────────

export const devMockClient = {
  from(table) {
    return new MockQuery(table);
  },
  auth,
  storage,
  rpc,
  functions,
};
